'use client';

import debounce from 'lodash/debounce';
import { usePathname, useRouter } from 'next/navigation';
import { FC, lazy, ReactElement, useCallback, useEffect, useMemo, useState } from 'react';

import AnimatedNextLink from '@/components/CustomNextLink';
import HeaderSupraIcon from '@/icons/headerSupra';
import HeaderSupraLightIcon from '@/icons/headerSupraLight';
import { supraBaseUrl } from '@/services/config';

// import useHeaderState from '@/utils/hooks/useHeaderState'
// import useRouteNavigation from '@/utils/hooks/useRouteNavigation'
// import useThemeState from '@/utils/hooks/useThemeState'
import Links from './Links';

const ToggleContent = lazy(() => import('./ToggleContent'));

// Optimize browser/OS detection with simpler checks
const detectBrowserAndOS = () => {
	const userAgent = navigator.userAgent;
	const isChrome = /Chrome/.test(userAgent);
	const isSafari = /Safari/.test(userAgent) && !/Chrome/.test(userAgent);
	const isWindows = /Windows/.test(userAgent);
	const isMacOS = /Mac OS/.test(userAgent);
	const isLinux = /Linux/.test(userAgent);

	return {
		browser: isChrome ? 'chrome' : isSafari ? 'safari' : 'other',
		os: isWindows ? 'windows' : isMacOS ? 'macos' : isLinux ? 'linux' : 'other',
	};
};

const Header: FC = (): ReactElement => {
	const pathName = usePathname();
	const isPathNameHome = pathName === '/';
	const [scrolling, setScrolling] = useState(false);
	const router = useRouter();

	// Reduce debounce delay to 50ms for faster response
	const handleScroll = useMemo(
		() =>
			debounce(() => {
				setScrolling(window.scrollY > 80);
			}, 50),
		[]
	);

	useEffect(() => {
		window.addEventListener('scroll', handleScroll);
		return () => {
			handleScroll.cancel(); // Cancel any pending debounce
			window.removeEventListener('scroll', handleScroll);
		};
	}, [handleScroll]);

	// Run browser/OS detection once on mount
	useEffect(() => {
		const { browser, os } = detectBrowserAndOS();
		const whichBrowser = document.documentElement;

		whichBrowser.classList.add(`${browser}-browser`);
		whichBrowser.classList.add(`${os}-os`);

		return () => {
			whichBrowser.classList.remove(`${browser}-browser`);
			whichBrowser.classList.remove(`${os}-os`);
		};
	}, []);

	// Simplify header className calculation
	const headerClassName = useMemo(() => {
		const baseClasses = '!fixed top-0 z-[9] w-full opacity-100';
		const scrollClasses = scrolling
			? 'bg-white dark:bg-[rgba(0,0,0,1)]'
			: 'bg-white backdrop-blur dark:bg-[rgba(0,0,0,0.8)] lg:!border-b-[1px] lg:border-solid lg:dark:!border-[#17293F] lg:dark:bg-[rgba(5,5,5,0.40)]';
		return `${baseClasses} ${scrollClasses}`;
	}, [scrolling]);

	// Prefetch all major routes for instant navigation
	useEffect(() => {
		router.prefetch('/data');
		router.prefetch('/data/[instrument]');
		router.prefetch('/networks');
		router.prefetch('/networks/[networkName]');
		router.prefetch('/providers');
		router.prefetch('/dvrf');
		router.prefetch('/indices');
		router.prefetch('/validators');
	}, [router]);

	return (
		<header data-testid="layout-header" id="layout-header" className={headerClassName}>
			<div className="flex w-28 cursor-pointer self-center sm:w-32">
				<AnimatedNextLink withUnderlineAnimation={false} href={supraBaseUrl} className="relative w-full">
					<div className="hidden dark:block">
						<HeaderSupraIcon className="h-full w-full" />
					</div>
					<div className="block dark:hidden">
						<HeaderSupraLightIcon className="h-full w-full" />
					</div>
				</AnimatedNextLink>
			</div>
			<div id="header-links-wrapper" className="hidden items-center justify-center xl:flex">
				<Links />
			</div>
			<div className="flex flex-row items-center justify-end py-2.5">
				<ToggleContent />
			</div>
		</header>
	);
};

export default Header;
